import { useState } from 'react'

import { format } from 'date-fns/format'
import capitalize from 'lodash/capitalize'
import { HeatMapGrid } from 'react-grid-heatmap'

import type { SelectOptionsType } from '@/components/ui/form/FormSelect'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Weekday } from '@/modules/overview/types'
import { useIsDarkTheme } from '@/utils/useTheme'

type StatisticsByHours = Record<string, number>

interface Props {
  earningsHeatmap?: Record<Weekday, StatisticsByHours>
  fanActivityHeatmap?: Record<Weekday, StatisticsByHours>
  isFetching: boolean
}

const heatMapOptions: SelectOptionsType = [
  { value: 'fanActivityHeatmap', label: 'Fan activity' },
  { value: 'earningsHeatmap', label: 'Earnings' },
]

export const Heatmap = ({
  earningsHeatmap,
  fanActivityHeatmap,
  isFetching,
}: Props) => {
  const [heatMapType, setHeatMapType] = useState(heatMapOptions[0].value)
  const isDark = useIsDarkTheme()

  const xLabelsValues: string[] = new Array(24).fill(0).map((_, i) => `${i}`)
  const yLabelsValues: Weekday[] = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
  ]
  const xLabels: string[] = xLabelsValues.map((hour) =>
    format(new Date(0, 0, 0, +hour), 'haaa'),
  )
  const yLabels: string[] = yLabelsValues.map((day) =>
    capitalize(day.slice(0, 3)),
  )

  const data = yLabelsValues.map((day) =>
    xLabelsValues.map(
      (hour) =>
        (heatMapType === 'earningsHeatmap'
          ? earningsHeatmap
          : fanActivityHeatmap)?.[day][hour] ?? 0,
    ),
  )

  const getTooltipContent = (value: number) => {
    return heatMapType === 'earningsHeatmap' ? `$${value}` : `${value}`
  }

  const skeleton = (
    <div className="h-96 w-full animate-pulse rounded-2xl bg-skeleton" />
  )

  return (
    <div className="bg-card max-w-full overflow-hidden rounded-lg p-8 pb-3">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-xl font-semibold">Day/Hour Heatmap</div>
        <Select
          onValueChange={setHeatMapType}
          value={heatMapType}
        >
          <SelectTrigger className="h-[38px] w-max">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {heatMapOptions.map(({ label, value }) => (
                <SelectItem
                  key={value}
                  value={value}
                >
                  <span>{label}</span>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="max-w-full overflow-auto">
        {isFetching ? (
          skeleton
        ) : (
          <HeatMapGrid
            data={data}
            xLabels={xLabels}
            yLabels={yLabels}
            xLabelsPos="bottom"
            cellRender={(y, x, value) => (
              <HeatMapCell
                x={xLabels[x]}
                y={yLabels[y]}
                value={getTooltipContent(value)}
                key={`${x}-${y}`}
              />
            )}
            cellHeight="46px"
            xLabelsStyle={(index) => ({
              color: index % 2 ? 'transparent' : undefined,
              fontSize: '14px',
              lineHeight: '36px',
              padding: 0,
              textAlign: 'left',
            })}
            yLabelsStyle={() => ({
              fontSize: '14px',
              width: 50,
              textAlign: 'left',
              padding: 0,
            })}
            cellStyle={(_x, _y, ratio) => {
              const opacity = Number.isNaN(ratio) || !ratio ? 0.12 : ratio

              return {
                background: 'hsl(var(--primary-400))',
                opacity,
                height: 46,
                width: 46,
                borderRadius: 0,
                borderWidth: '2px 2px 0px 0px',
                borderColor: isDark ? 'hsl(var(--secondary-700))' : undefined,
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

const HeatMapCell = ({
  x,
  y,
  value,
}: {
  x: string
  y: string
  value: string
}) => {
  const [open, setOpen] = useState(false)

  const handleMouseEnter = () => {
    setOpen(true)
  }

  const handleMouseLeave = () => {
    setOpen(false)
  }

  return (
    <Popover open={open}>
      <PopoverTrigger
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="h-[46px] w-[46px]" />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={-5}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="w-max p-2 outline-none"
      >
        <div>
          <div className="text-xs font-medium leading-5">
            {x}, {y}
          </div>
          <div className="text-secondary-300 text-[10px] font-medium leading-[14px]">
            {value}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
